import type { BuilderKinds, CramMode, ExerciseKind, SessionCard } from './types';

// Полный набор для старых сессий с 'random' или без сохранённого состава.
const BUILDER_ALL: ExerciseKind[] = ['input', 'reverse', 'mcq', 'cloze'];

/** Стабильный (детерминированный по itemId) хэш — «рандом» формата на карточку:
 *  один и тот же тип упражнения на повторных рендерах одной карточки, но разный
 *  разброс между карточками. itemId стабилен в пределах сессии (включая requeue),
 *  поэтому формат карточки не «прыгает» между перерисовками. */
function hashItemId(itemId: string): number {
  let h = 0;
  for (let i = 0; i < itemId.length; i++) h = (h * 31 + itemId.charCodeAt(i)) | 0;
  return Math.abs(h);
}

/** «Жёсткое воспроизведение» — общая логика для подготовки к экзамену (cram
 *  'whole') и review-стадии обычной сессии. Только два формата:
 *   - MANUAL → обратный перевод (бэк проверит ТОЧНЫМ совпадением по front_name);
 *   - LLM без примера → обратный перевод (cloze без предложения нечего показать);
 *   - LLM с примером → стабильный рандом контекст(cloze)/обратный перевод —
 *     разбавляем чистый cloze воспроизведением. Выбора-из-4 и ввода тут нет. */
function hardRecall(card: SessionCard): ExerciseKind {
  if (!card.isLlmGenerated) return 'reverse';
  if (!card.cloze) return 'reverse';
  return hashItemId(card.itemId) % 2 === 0 ? 'cloze' : 'reverse';
}

/** Тянет ли карточка данный тип упражнения. cloze требует предложения
 *  (card.cloze выводится из example_en) — без него рендерить нечего. mcq требует
 *  серверных selectionOptions: дистракторы теперь собирает бэкенд (1 верный + до 3
 *  из словаря, отфильтрованные так, чтобы ни один не прошёл грейдер). Если бэк их не
 *  дал (пустой словарный пул) — mcq НЕ показываем (иначе фронт свалился бы на
 *  хардкод-DISTRACTORS, которые грейдер может засчитать верными → ложный «неверно»). */
export function cardSupports(card: SessionCard, kind: ExerciseKind): boolean {
  if (kind === 'cloze') return card.cloze !== null;
  if (kind === 'mcq') return (card.selectionOptions?.length ?? 0) >= 2;
  return true;
}

/** Формат каждой карточки случайно выбирается среди доступных выбранных типов.
 *  Источник случайности — UUID item'а, который бэкенд создаёт для новой сессии.
 *  Хэш сохраняет назначение при рендерах, повторах и возобновлении; позиция
 *  в очереди и прогресс других карточек на него не влияют. Старое 'random'
 *  означает тот же выбор из всех четырёх форматов. Если ни один выбранный
 *  формат карточка не поддерживает (в конструкторе только «Пропущено слово», а у
 *  ручной карточки нет предложения) — берём любой доступный ей формат (2026-09-25). */
function builderExercise(
  card: SessionCard,
  kinds: BuilderKinds,
): ExerciseKind {
  const pool = !kinds || kinds === 'random' ? BUILDER_ALL : kinds;
  const chosen = pool.filter((k) => cardSupports(card, k));
  const supported = chosen.length > 0 ? chosen : BUILDER_ALL.filter((k) => cardSupports(card, k));
  if (supported.length === 0) return 'input';
  return supported[hashItemId(card.itemId) % supported.length];
}

/**
 * Состав упражнений определяет конструктор. Старые режимы оставлены для
 * возобновления ранее начатых тренировок; состояния FSRS не выбирают режим.
 */
export function exerciseFor(
  card: SessionCard,
  cramMode?: CramMode | null,
  builderKinds?: BuilderKinds,
): ExerciseKind {
  // ── Самостоятельная практика: набор упражнений зависит от режима ──
  switch (cramMode) {
    case 'recent':
      // A · «ввод + контекст»: cloze если есть пример, иначе ввод. MANUAL → ввод.
      if (!card.isLlmGenerated) return 'input';
      return card.cloze ? 'cloze' : 'input';
    case 'weak':
      // B · в UI «в разработке», логику оставляем: review с примером → cloze,
      // без примера → mcq (если бэк дал варианты, иначе ввод); new/learning → ввод.
      if (!card.isLlmGenerated) return 'input';
      if (card.state === 'review') {
        if (card.cloze) return 'cloze';
        return cardSupports(card, 'mcq') ? 'mcq' : 'input';
      }
      return 'input';
    case 'random':
      // D · убран из UI, логику оставляем: ввод для новых, иначе mcq (если есть варианты).
      if (!card.isLlmGenerated) return 'input';
      if (card.state === 'new') return 'input';
      return cardSupports(card, 'mcq') ? 'mcq' : 'input';
    case 'whole':
      // C · Подготовка к экзамену: только контекст + обратный перевод.
      return hardRecall(card);
    case 'builder':
      // «Тренировка»: состав выбрал юзер (builderKinds приходит из сессии и
      // переживает перезагрузку). Если состав не дошёл — все форматы.
      return builderExercise(card, builderKinds ?? 'random');
    default:
      return builderExercise(card, builderKinds ?? 'random');
  }
}
